import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { shouldSkipInvalidation } from '@/lib/core/visibility-tracker';
import type {
  ShiftSchedulingActivityEventWithDetails,
  ShiftSchedulingActivityFilters,
  ShiftSchedulingEventType,
} from '@/types/shift-scheduling-activity';
import { startOfDay, endOfDay, isToday, isYesterday, format } from 'date-fns';

const DEFAULT_LIMIT = 50;

interface UseShiftSchedulingActivityLogOptions {
  businessId: string | null;
  filters?: ShiftSchedulingActivityFilters;
  limit?: number;
}

interface UseShiftSchedulingActivityLogResult {
  events: ShiftSchedulingActivityEventWithDetails[];
  isLoading: boolean;
  error: Error | null;
  refetch: () => void;
  groupedEvents: Map<string, ShiftSchedulingActivityEventWithDetails[]>;
}

/**
 * Hook for fetching and subscribing to shift scheduling activity log
 * Provides real-time updates via Supabase realtime subscriptions
 */
export function useShiftSchedulingActivityLog({
  businessId,
  filters,
  limit = DEFAULT_LIMIT,
}: UseShiftSchedulingActivityLogOptions): UseShiftSchedulingActivityLogResult {
  const queryClient = useQueryClient();

  const queryKey = useMemo(
    () => ['shift-scheduling-activity-log', businessId, filters, limit],
    [businessId, filters, limit]
  );

  // Build date range filter
  const dateRange = useMemo(() => {
    if (filters?.dateRange) {
      return {
        start: startOfDay(filters.dateRange.start).toISOString(),
        end: endOfDay(filters.dateRange.end).toISOString(),
      };
    }
    // Default to last 7 days
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - 7);
    return {
      start: startOfDay(start).toISOString(),
      end: endOfDay(end).toISOString(),
    };
  }, [filters?.dateRange]);

  // Fetch activity log with employee data
  const { data, isLoading, error, refetch } = useQuery({
    queryKey,
    queryFn: async () => {
      if (!businessId) return [];

      // First fetch activity logs
      let query = supabase
        .from('shift_scheduling_activity_log')
        .select('*')
        .eq('business_id', businessId)
        .gte('event_timestamp', dateRange.start)
        .lte('event_timestamp', dateRange.end)
        .order('event_timestamp', { ascending: false })
        .limit(limit);

      // Apply event type filter
      if (filters?.eventTypes && filters.eventTypes.length > 0) {
        query = query.in('event_type', filters.eventTypes);
      }

      // Apply actor type filter
      if (filters?.actorTypes && filters.actorTypes.length > 0) {
        query = query.in('actor_type', filters.actorTypes);
      }

      // Apply employee filter (matches target_employee_id)
      if (filters?.employeeIds && filters.employeeIds.length > 0) {
        query = query.in('target_employee_id', filters.employeeIds);
      }

      const { data: events, error: queryError } = await query;

      if (queryError) throw queryError;

      if (!events || events.length === 0) return [];

      // Get unique employee IDs from actor_employee_id and target_employee_id
      const actorEmployeeIds = [...new Set(events.map(e => e.actor_employee_id).filter(Boolean))] as string[];
      const targetEmployeeIds = [...new Set(events.map(e => e.target_employee_id).filter(Boolean))] as string[];
      const allEmployeeIds = [...new Set([...actorEmployeeIds, ...targetEmployeeIds])];

      // Fetch employee data
      let employeeMap = new Map<string, { id: string; name: string; avatar_url?: string | null }>();

      if (allEmployeeIds.length > 0) {
        const { data: employees } = await supabase
          .from('employees')
          .select('id, name, avatar_url')
          .in('id', allEmployeeIds);

        employeeMap = new Map(employees?.map(e => [e.id, e]) || []);
      }

      // Combine data
      return events.map(event => ({
        ...event,
        actor_employee: event.actor_employee_id ? employeeMap.get(event.actor_employee_id) || null : null,
        target_employee: event.target_employee_id ? employeeMap.get(event.target_employee_id) || null : null,
      })) as ShiftSchedulingActivityEventWithDetails[];
    },
    enabled: !!businessId,
    staleTime: 30000, // 30 seconds
  });

  // Real-time subscription for new events
  useEffect(() => {
    if (!businessId) return;

    const channel = supabase
      .channel(`shift-scheduling-activity-${businessId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'shift_scheduling_activity_log',
          filter: `business_id=eq.${businessId}`,
        },
        () => {
          // Skip invalidation if tab just became visible (prevents refetch storm)
          if (shouldSkipInvalidation()) return;
          // Invalidate and refetch on new events
          queryClient.invalidateQueries({ queryKey });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [businessId, queryClient, queryKey]);

  // Group events by date
  const groupedEvents = useMemo(() => {
    const groups = new Map<string, ShiftSchedulingActivityEventWithDetails[]>();

    (data || []).forEach((event) => {
      const eventDate = new Date(event.event_timestamp);
      let groupKey: string;

      if (isToday(eventDate)) {
        groupKey = 'Today';
      } else if (isYesterday(eventDate)) {
        groupKey = 'Yesterday';
      } else {
        groupKey = format(eventDate, 'EEEE, MMMM d');
      }

      if (!groups.has(groupKey)) {
        groups.set(groupKey, []);
      }
      groups.get(groupKey)!.push(event);
    });

    return groups;
  }, [data]);

  return {
    events: data || [],
    isLoading,
    error: error as Error | null,
    refetch,
    groupedEvents,
  };
}

/**
 * Generate a human-readable description for a scheduling activity event
 */
export function getSchedulingActivityDescription(
  event: ShiftSchedulingActivityEventWithDetails
): string {
  // Use the stored description if available
  if (event.description) {
    return event.description;
  }

  const targetEmployeeName = event.target_employee?.name || 'Unknown employee';
  const shiftTitle = event.shift_title || 'Untitled shift';
  const metadata = event.metadata || {};

  switch (event.event_type) {
    case 'shift_created':
      return `Created shift "${shiftTitle}"`;

    case 'shift_edited': {
      const changes = metadata.changes || {};
      const changedFields: string[] = [];
      if (changes.title) changedFields.push('title');
      if (changes.notes) changedFields.push('notes');
      if (changes.breaks) changedFields.push('breaks');
      return `Edited ${changedFields.length ? changedFields.join(', ') : 'shift details'}`;
    }

    case 'shift_moved': {
      if (metadata.previousStart && metadata.newStart) {
        const prevStart = new Date(metadata.previousStart);
        const newStart = new Date(metadata.newStart);
        const sameDayMove = format(prevStart, 'yyyy-MM-dd') === format(newStart, 'yyyy-MM-dd');

        if (sameDayMove) {
          return `Moved shift from ${format(prevStart, 'h:mm a')} to ${format(newStart, 'h:mm a')}`;
        }
        return `Moved shift from ${format(prevStart, 'MMM d, h:mm a')} to ${format(newStart, 'MMM d, h:mm a')}`;
      }
      return 'Moved shift';
    }

    case 'shift_deleted':
      return `Deleted shift "${shiftTitle}"`;

    case 'employee_assigned': {
      if (metadata.previousEmployeeName) {
        return `Reassigned from ${metadata.previousEmployeeName} to ${targetEmployeeName}`;
      }
      return `Assigned ${targetEmployeeName}`;
    }

    case 'employee_unassigned':
      return `Unassigned ${targetEmployeeName}`;

    case 'employee_self_requested':
      return `${targetEmployeeName} requested this shift`;

    case 'employee_request_approved':
      return `Approved ${targetEmployeeName}'s request`;

    case 'employee_request_rejected':
      return `Rejected ${targetEmployeeName}'s request`;

    default:
      return `Activity on shift "${shiftTitle}"`;
  }
}
